import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
  }

  // Initialize WebSocket connection
  connect(adminId, companyId) {
    if (this.socket && this.isConnected) {
      return;
    }

    try {
      this.socket = io(API_BASE_URL, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true
      });

      this.socket.on('connect', () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        this.socket.emit('join-company', { 
          companyId,
          adminId 
        });
        
        this.socket.emit('join-dashboard', { adminId });
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnected = false;
        
        if (reason !== 'io client disconnect') {
          this.attemptReconnect(adminId, companyId);
        }
      });

      this.socket.on('connect_error', (error) => {
        this.isConnected = false;
        this.attemptReconnect(adminId, companyId);
      });

      // Dashboard event handlers
      this.socket.on('dashboard-stats-update', (stats) => {
        this.emit('dashboard-stats-update', stats);
      });

      this.socket.on('recent-activity-update', (activity) => {
        this.emit('recent-activity-update', activity);
      });

      this.socket.on('weekly-activity-update', (activityData) => {
        this.emit('weekly-activity-update', activityData);
      });

      this.socket.on('personnel-checkin', (checkInData) => {
        this.emit('personnel-checkin', checkInData);
      });

      this.socket.on('personnel-checkout', (checkOutData) => {
        this.emit('personnel-checkout', checkOutData);
      });

      this.socket.on('new-report', (reportData) => {
        this.emit('new-report', reportData);
      });

      this.socket.on('personnel-status-change', (personnelData) => {
        this.emit('personnel-status-change', personnelData);
      });

      this.socket.on('dashboard-refresh', () => {
        this.emit('dashboard-refresh');
      });

      this.socket.on('personnel-notification-individual', (data) => {
        this.emit('personnel-notification-individual', data);
      });

      this.socket.on('personnel-added', (notificationData) => {
        this.emit('personnel-added', notificationData);
      });

      this.socket.on('personnel-updated', (notificationData) => {
        this.emit('personnel-updated', notificationData);
      });

      this.socket.on('personnel-disabled', (notificationData) => {
        this.emit('personnel-disabled', notificationData);
      });

      this.socket.on('personnel-deleted', (notificationData) => {
        this.emit('personnel-deleted', notificationData);
      });

      // Site management events
      this.socket.on('site-notification-individual', (data) => {
        this.emit('site-notification-individual', data);
      });

      this.socket.on('site-added', (notificationData) => {
        this.emit('site-added', notificationData);
      });

      this.socket.on('site-updated', (notificationData) => {
        this.emit('site-updated', notificationData);
      });

      this.socket.on('site-deleted', (notificationData) => {
        this.emit('site-deleted', notificationData);
      });

      // Structure management events
      this.socket.on('structure-notification-individual', (data) => {
        this.emit('structure-notification-individual', data);
      });

      this.socket.on('branch-added', (notificationData) => {
        this.emit('branch-added', notificationData);
      });

      this.socket.on('region-added', (notificationData) => {
        this.emit('region-added', notificationData);
      });

      this.socket.on('site-assigned', (notificationData) => {
        this.emit('site-assigned', notificationData);
      });

      // Coverage events
      this.socket.on('coverage-notification-individual', (data) => {
        this.emit('coverage-notification-individual', data);
      });

      this.socket.on('coverage-submitted', (notificationData) => {
        this.emit('coverage-submitted', notificationData);
      });

      this.socket.on('coverage-edited', (notificationData) => {
        this.emit('coverage-edited', notificationData);
      });

      // Client events
      this.socket.on('client-notification-individual', (data) => {
        this.emit('client-notification-individual', data);
      });

      this.socket.on('client-added', (notificationData) => {
        this.emit('client-added', notificationData);
      });

      // Incident report events
      this.socket.on('incident-notification-individual', (data) => {
        this.emit('incident-notification-individual', data);
      });

      this.socket.on('incident-submitted', (notificationData) => {
        this.emit('incident-submitted', notificationData);
      });

      this.socket.on('incident-status-updated', (notificationData) => {
        this.emit('incident-status-updated', notificationData);
      });

      // Special assignment events
      this.socket.on('assignment-notification-individual', (data) => {
        this.emit('assignment-notification-individual', data);
      });

      this.socket.on('assignment-submitted', (notificationData) => {
        this.emit('assignment-submitted', notificationData);
      });

      this.socket.on('assignment-assigned', (notificationData) => {
        this.emit('assignment-assigned', notificationData);
      });

      this.socket.on('assignment-deleted', (notificationData) => {
        this.emit('assignment-deleted', notificationData);
      });

      this.socket.on('assignment-approved', (notificationData) => {
        this.emit('assignment-approved', notificationData);
      });

      this.socket.on('assignment-accepted', (notificationData) => {
        this.emit('assignment-accepted', notificationData);
      });

      this.socket.on('assignment-rejected', (notificationData) => {
        this.emit('assignment-rejected', notificationData);
      });

      this.socket.on('assignment-completed', (notificationData) => {
        this.emit('assignment-completed', notificationData);
      });

      // General notification event
      this.socket.on('admin-notification', (notificationData) => {
        this.emit('admin-notification', notificationData);
      });

    } catch (error) {
      // Silent error handling
    }
  }

  emitPersonnelAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('personnel-action', { action, data });
    }
  }

  emitSiteAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('site-action', { action, data });
    }
  }

  emitStructureAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('structure-action', { action, data });
    }
  }

  emitCoverageAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('coverage-action', { action, data });
    }
  }

  emitClientAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('client-action', { action, data });
    }
  }

  emitIncidentAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('incident-action', { action, data });
    }
  }

  emitAssignmentAction(action, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('assignment-action', { action, data });
    }
  }

  attemptReconnect(adminId, companyId) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      this.connect(adminId, companyId);
    }, delay);
  }

  // Disconnect WebSocket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          // Silent error handling
        }
      });
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket?.id
    };
  }
}

// Export singleton instance
export default new WebSocketService();
